import {
  ChevronDown,
  ChevronsLeft,
  ChevronsRight,
  HelpCircle,
  Lock,
  LogOut,
  Menu,
  User,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useState, type ReactNode } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import Logo from "@/components/ui/Logo";
import Button from "@/components/ui/Button";
import Modal from "@/components/ui/Modal";
import { clearSession } from "@/components/feature/auth/session";
import { usePlatformSettings } from "@/services/platformSettings";
import NotificationBell from "@/layouts/NotificationBell";

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  badge?: number;
  dot?: boolean;
}

export interface ShellUser {
  name: string;
  role: string;
  initials: string;
}

interface AppShellProps {
  navItems: NavItem[];
  user: ShellUser;
  breadcrumb: string;
  adminBadge?: boolean;
  topbarExtra?: ReactNode;
  topBanner?: ReactNode;
  createMandateDisabled?: boolean;
}

export default function AppShell({
  navItems,
  user,
  breadcrumb,
  adminBadge,
  topbarExtra,
  topBanner,
  createMandateDisabled,
}: AppShellProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const platformSettings = usePlatformSettings();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);

  const profileBase = adminBadge ? "/admin/profile" : "/retailer/profile";

  function handleLogout() {
    setUserMenuOpen(false);
    setLogoutOpen(true);
  }

  function confirmLogout() {
    setLogoutOpen(false);
    clearSession();
    navigate("/login");
  }

  function handleUserNav(to: string) {
    setUserMenuOpen(false);
    if (to === "logout") {
      handleLogout();
    } else {
      navigate(to);
    }
  }

  const sidebarWidth = collapsed ? "lg:w-[72px]" : "lg:w-[260px]";

  const sidebarContent = (
    <div className="flex h-full flex-col bg-sidebar">
      <div className={`flex items-center gap-2 px-4 py-5 ${collapsed ? "justify-center" : "justify-between"}`}>
        <div className={collapsed ? "" : "flex items-center"}>
          {collapsed ? (
            <Logo wordmark={false} size="md" />
          ) : (
            <Logo variant="light" size="lg" />
          )}
        </div>
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          className="hidden text-slate-400 hover:text-white lg:flex h-8 w-8 items-center justify-center rounded-md cursor-pointer"
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? <ChevronsRight className="h-5 w-5" /> : <ChevronsLeft className="h-5 w-5" />}
        </button>
      </div>

      {adminBadge && !collapsed && (
        <div className="px-4 pb-4">
          <span className="inline-flex items-center rounded-full bg-primary-500 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white">
            Admin
          </span>
        </div>
      )}
      {adminBadge && collapsed && (
        <div className="flex justify-center pb-4">
          <span className="h-1.5 w-1.5 rounded-full bg-primary-400" />
        </div>
      )}

      <div className="mx-4 border-t border-white/10" />

      <nav className="mt-4 flex flex-1 flex-col gap-1 px-2">
        {navItems.map((item) => {
          const active =
            location.pathname === item.to ||
            (item.to !== "/" && location.pathname.startsWith(item.to));
           
          return (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setMobileOpen(false)}
              className={`flex h-11 items-center rounded-lg transition-colors ${
                collapsed ? "justify-center px-0 mx-1" : "gap-3 px-3 mx-1"
              } ${
                active
                  ? "bg-primary-500 text-white"
                  : "text-sidebar-text hover:bg-sidebar-hover hover:text-white"
              }`}
              title={collapsed ? item.label : undefined}
            >
              <span className="flex w-5 items-center justify-center">
                <item.icon className="h-5 w-5" />
              </span>
              {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
              {!collapsed && item.dot && (
                <span className="ml-auto h-2 w-2 rounded-full bg-amber-400" />
              )}
              {!collapsed && item.badge !== undefined && item.badge > 0 && (
                <span className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-danger px-1.5 text-[11px] font-semibold text-white">
                  {item.badge}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      {!collapsed && (
        <div className="p-3">
          <div className="rounded-lg bg-sidebar-hover p-3">
            <div className="flex items-start gap-2.5">
              <HelpCircle className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" />
              <div>
                <p className="text-[13px] font-semibold text-white">Need help?</p>
                <a
                  href={`mailto:${platformSettings.supportEmail}`}
                  className="text-[11px] text-sidebar-text hover:text-white"
                  rel="nofollow"
                >
                  {platformSettings.supportEmail}
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
      {collapsed && (
        <div className="flex justify-center p-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-sidebar-hover text-slate-400">
            <HelpCircle className="h-5 w-5" />
          </span>
        </div>
      )}
    </div>
  );

  return (
    <div className="flex h-screen w-full overflow-hidden bg-page">
      {/* Desktop sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-30 hidden lg:block ${sidebarWidth} transition-all duration-200`}
      >
        {sidebarContent}
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-[280px] shadow-dropdown animate-slideIn">
            {sidebarContent}
          </aside>
        </div>
      )}

      {/* Main column */}
      <div
        className={`flex h-full w-full flex-col transition-all duration-200 ${
          collapsed ? "lg:pl-[72px]" : "lg:pl-[260px]"
        }`}
      >
        {/* Top bar */}
        <header className="z-20 flex h-16 shrink-0 items-center justify-between border-b border-line bg-white px-4 md:px-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="flex h-9 w-9 items-center justify-center rounded-md text-ink-secondary hover:bg-slate-100 lg:hidden cursor-pointer"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
            <h2 className="text-[15px] font-semibold text-ink">{breadcrumb}</h2>
          </div>

          <div className="flex items-center gap-3">
            {topbarExtra}

            {!adminBadge &&
              !location.pathname.startsWith("/retailer/create-mandate") && (
                <div
                  className="relative"
                  title={createMandateDisabled ? "Complete your KYC first" : undefined}
                >
                  <button
                    type="button"
                    disabled={createMandateDisabled}
                    onClick={() => navigate("/retailer/create-mandate")}
                    className="hidden h-10 items-center gap-1.5 rounded-lg bg-primary-500 px-4 text-sm font-medium text-white hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50 sm:inline-flex cursor-pointer"
                  >
                    <span className="text-lg leading-none">+</span>
                    Create Mandate
                  </button>
                </div>
              )}

            <NotificationBell adminBadge={adminBadge} />

            {/* User menu */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setUserMenuOpen((v) => !v)}
                className="flex items-center gap-2.5 rounded-lg p-1.5 hover:bg-slate-100 cursor-pointer"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-50 text-sm font-semibold text-primary-600">
                  {user.initials}
                </span>
                <span className="hidden text-left md:block">
                  <span className="block text-[13px] font-medium leading-tight text-ink">{user.name}</span>
                  <span className="block text-[11px] leading-tight text-ink-secondary">{user.role}</span>
                </span>
                <ChevronDown className="hidden h-4 w-4 text-ink-muted md:block" />
              </button>

              {userMenuOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setUserMenuOpen(false)} />
                  <div className="absolute right-0 z-20 mt-2 w-56 rounded-lg border border-line bg-white py-1.5 shadow-dropdown animate-fadeIn">
                    <div className="border-b border-line px-4 py-2.5 md:hidden">
                      <p className="text-[13px] font-medium text-ink">{user.name}</p>
                      <p className="text-[11px] text-ink-secondary">{user.role}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleUserNav(profileBase)}
                      className="flex w-full items-center gap-2.5 px-4 py-2 text-sm text-ink hover:bg-slate-50 cursor-pointer"
                    >
                      <User className="h-4 w-4 text-ink-muted" />
                      My Profile
                    </button>
                    <button
                      type="button"
                      onClick={() => handleUserNav(`${profileBase}?section=password`)}
                      className="flex w-full items-center gap-2.5 px-4 py-2 text-sm text-ink hover:bg-slate-50 cursor-pointer"
                    >
                      <Lock className="h-4 w-4 text-ink-muted" />
                      Change Password
                    </button>
                    <div className="my-1 border-t border-line" />
                    <button
                      type="button"
                      onClick={() => handleUserNav("logout")}
                      className="flex w-full items-center gap-2.5 px-4 py-2 text-sm text-danger hover:bg-danger-bg cursor-pointer"
                    >
                      <LogOut className="h-4 w-4" />
                      Logout
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {topBanner}

        <main className="mx-auto h-full w-full max-w-[1440px] flex-1 overflow-y-auto p-4 md:p-6">
          <Outlet />
        </main>
      </div>

      <Modal
        open={logoutOpen}
        onClose={() => setLogoutOpen(false)}
        title="Log out?"
        size="sm"
        footer={
          <div className="flex items-center justify-end gap-2.5 border-t border-line px-5 py-4">
            <Button variant="secondary" onClick={() => setLogoutOpen(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmLogout}>
              Log Out
            </Button>
          </div>
        }
      >
        <p className="text-sm text-ink-secondary">You will need to sign in again to continue.</p>
      </Modal>
    </div>
  );
}